/**
 * useDatasources Hook
 * Fetches and maps datasources from backend to platform objects
 */

import { useState, useEffect } from 'react';
import { datasourceService } from '../services/datasources.service';
import { Datasource, Platform, PLATFORM_COLORS } from '../types/platform.types';

// Platform logos from public folder
const salesforceLogo = '/logos/salesforce.png';
const sapLogo = '/logos/sap.png';
const odooLogo = '/logos/odoo.png';
const allPlatformsLogo = 'https://elama.ai/assets/elama.png'; // eLama logo for "all platforms"

export function useDatasources() {
  const [datasources, setDatasources] = useState<Datasource[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDatasources();
  }, []);

  const loadDatasources = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await datasourceService.listDatasources();
      setDatasources(data);

      // Map datasources to platforms
      const platformMap = mapDatasourcesToPlatforms(data);
      setPlatforms(platformMap);
    } catch (err: any) {
      setError(err.message || 'Failed to load datasources');
      console.error('Error loading datasources:', err);

      // Set default platforms even on error
      setPlatforms(getDefaultPlatforms());
    } finally {
      setLoading(false);
    }
  };

  return {
    datasources,
    platforms,
    loading,
    error,
    refresh: loadDatasources,
  };
}

/**
 * Map backend datasources to frontend platform objects
 */
function mapDatasourcesToPlatforms(datasources: Datasource[]): Platform[] {
  const salesforce = datasources.find((d) => d.type === 'salesforce');
  const sap = datasources.find((d) => d.type === 'sap_erp');
  const odoo = datasources.find((d) => d.type === 'odoo');

  return [
    {
      id: 'all',
      name: 'all',
      displayName: 'All Platforms',
      logo: allPlatformsLogo,
      color: PLATFORM_COLORS.all.primary,
      available: true, // Always available
      connectionStatus: 'active',
      connectionName: 'All Available Sources',
    },
    {
      id: 'salesforce',
      name: 'salesforce',
      displayName: 'Salesforce',
      logo: salesforceLogo,
      color: PLATFORM_COLORS.salesforce.primary,
      available: salesforce?.status === 'active',
      datasourceId: salesforce?.id,
      connectionStatus: salesforce?.status as any,
      lastSyncAt: salesforce?.last_sync_at,
      lastSyncStatus: salesforce?.last_sync_status === 'success' ? 'success' : 'error',
      connectionName: salesforce?.name,
      errorMessage: salesforce?.last_sync_error,
      metadata: {
        totalQueries: salesforce?.total_queries,
        avgResponseTime: salesforce?.avg_response_time_ms,
      },
    },
    {
      id: 'sap',
      name: 'sap',
      displayName: 'SAP',
      logo: sapLogo,
      color: PLATFORM_COLORS.sap.primary,
      available: sap?.status === 'active',
      datasourceId: sap?.id,
      connectionStatus: sap?.status as any,
      lastSyncAt: sap?.last_sync_at,
      lastSyncStatus: sap?.last_sync_status === 'success' ? 'success' : 'error',
      connectionName: sap?.name,
      errorMessage: sap?.last_sync_error,
      metadata: {
        totalQueries: sap?.total_queries,
        avgResponseTime: sap?.avg_response_time_ms,
      },
    },
    {
      id: 'odoo',
      name: 'odoo',
      displayName: 'Odoo',
      logo: odooLogo,
      color: PLATFORM_COLORS.odoo.primary,
      available: odoo?.status === 'active',
      datasourceId: odoo?.id,
      connectionStatus: odoo?.status as any,
      lastSyncAt: odoo?.last_sync_at,
      lastSyncStatus: odoo?.last_sync_status === 'success' ? 'success' : 'error',
      connectionName: odoo?.name,
      errorMessage: odoo?.last_sync_error,
      metadata: {
        totalQueries: odoo?.total_queries,
        avgResponseTime: odoo?.avg_response_time_ms,
      },
    },
  ];
}

/**
 * Get default platforms with "not configured" status
 * Used as fallback when datasources fail to load
 */
function getDefaultPlatforms(): Platform[] {
  return [
    {
      id: 'all',
      name: 'all',
      displayName: 'All Platforms',
      logo: allPlatformsLogo,
      color: PLATFORM_COLORS.all.primary,
      available: true,
    },
    {
      id: 'salesforce',
      name: 'salesforce',
      displayName: 'Salesforce',
      logo: salesforceLogo,
      color: PLATFORM_COLORS.salesforce.primary,
      available: false,
    },
    {
      id: 'sap',
      name: 'sap',
      displayName: 'SAP',
      logo: sapLogo,
      color: PLATFORM_COLORS.sap.primary,
      available: false,
    },
    {
      id: 'odoo',
      name: 'odoo',
      displayName: 'Odoo',
      logo: odooLogo,
      color: PLATFORM_COLORS.odoo.primary,
      available: false,
    },
  ];
}
